import { Fragment } from "react";
import { X } from "lucide-react";
import { Group, Option, CategoryTree, BrandFilter, PriceFilter } from "./FilterParts";
import { SPEC_KEYS } from "./useListing";

const fmt = (n) => `$${Number(n).toLocaleString("en-AU")}`;

// one filter panel for All Products, category, brand and search (T12.c)
export function FilterPanel({ data, L, hideBrand, baseLabel }) {
  const f = data.facets || {};
  const a = data.applied_filters || {};
  const brandSel = L.list("brand");
  const avail = L.list("avail").length ? L.list("avail") : (a.in_stock ? ["in_stock"] : []);
  return (
    <div data-testid="facet-sidebar">
      {f.category && (f.category.options.length > 0 || (f.category.breadcrumbs || []).length > 0) && (
        <Group id="category" title="Category">
          <CategoryTree facet={f.category} base={baseLabel} onPick={(slug) => L.set("cat", slug)} />
        </Group>)}
      {!hideBrand && f.brand?.length > 0 && (
        <Group id="brand" title="Brand" count={brandSel.length}>
          <BrandFilter brands={f.brand} selected={brandSel} onToggle={(v) => L.toggle("brand", v)} />
        </Group>)}
      <Group id="price" title="Price" count={a.price_min != null || a.price_max != null ? 1 : 0}>
        <PriceFilter hist={f.price_hist} min={a.price_min} max={a.price_max} gst={a.gst || "inc"} onPrice={L.setPrice} onGst={(g) => L.set("gst", g === "ex" ? "ex" : null)} />
      </Group>
      {f.avail && (
        <Group id="availability" title="Availability" count={avail.length}>
          {f.avail.map((o) => <Option key={o.key} testid={`facet-avail-${o.key}`} label={o.display_name} count={o.count}
            checked={avail.includes(o.key)} disabled={o.disabled} onChange={() => L.toggle("avail", o.key)} />)}
        </Group>)}
      {(f.specs || []).map((s) => (
        <Group key={s.key} id={`spec-${s.key}`} title={s.display_name} count={L.list(s.key).length} defaultOpen={(f.specs || []).indexOf(s) < 4}>
          {s.values.map((v) => <Option key={v.key} testid={`facet-spec-${s.key}-${v.key.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} label={v.display_name} count={v.count}
            checked={L.list(s.key).includes(v.key)} disabled={v.disabled} onChange={() => L.toggle(s.key, v.key)} />)}
          <div className="mt-1 text-[11px] text-slate-400">Known for {Math.round(s.coverage * 100)}% of {s.of.toLocaleString("en-AU")} matching products</div>
        </Group>))}
    </div>
  );
}

// applied filters as removable chips (+ labels reused by the empty state)
export function appliedChips(data, L) {
  const f = data.facets || {}, a = data.applied_filters || {};
  const out = [];
  const brands = Object.fromEntries((f.brand || []).map((b) => [b.slug, b.display_name]));
  L.list("brand").forEach((s) => out.push({ id: `brand-${s}`, label: brands[s] || s, dim: "brand", off: () => L.toggle("brand", s) }));
  if (a.cat) out.push({ id: "cat", label: (f.category?.breadcrumbs || []).slice(-1)[0]?.display_name?.toLowerCase() || a.cat, dim: "category", off: () => L.set("cat", null) });
  if (a.price_min != null || a.price_max != null) out.push({ id: "price", dim: "price", off: () => L.setPrice(null, null),
    label: a.price_min != null && a.price_max != null ? `${fmt(a.price_min)} – ${fmt(a.price_max)}` : a.price_min != null ? `From ${fmt(a.price_min)}` : `Up to ${fmt(a.price_max)}` });
  const av = L.list("avail");
  av.forEach((k) => out.push({ id: `avail-${k}`, label: k === "in_stock" ? "In stock" : "Out of stock", dim: "availability", off: () => L.toggle("avail", k) }));
  if (a.in_stock && !av.length) out.push({ id: "in_stock", label: "In stock", dim: "availability", off: () => L.remove(["in_stock"]) });
  SPEC_KEYS.forEach((k) => L.list(k).forEach((v) => out.push({ id: `${k}-${v}`, label: v, dim: `spec:${k}`, off: () => L.toggle(k, v) })));
  if (a.gst === "ex") out.push({ id: "gst", label: "Prices ex GST", dim: null, off: () => L.set("gst", null) });
  return out;
}

export const SORTS = [["relevance", "Best match"], ["price_asc", "Price: low to high"], ["price_desc", "Price: high to low"], ["name", "Name A–Z"]];
export const SIZES = [24, 48, 96];

export function AppliedChips({ chips, onClear }) {
  if (!chips.length) return null;
  return (  /* T75: one slim scrollable row on phones (Clear all stays pinned at the end), wraps from md up */
    <div className="-mx-1 flex items-center gap-2 overflow-x-auto px-1 pb-0.5 [scrollbar-width:none] md:flex-wrap md:overflow-visible" data-testid="applied-filters" aria-label="Applied filters">
      {chips.map((c) => (
        <button key={c.id} type="button" onClick={c.off} data-testid={`applied-chip-${c.id.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} aria-label={`Remove filter ${c.label}`}
          className="group inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-[var(--brand-primary)] bg-[var(--brand-primary-tint)] px-3 py-1 text-xs font-bold text-[var(--brand-primary)] transition-colors hover:bg-[var(--brand-primary)] hover:text-white">
          <span className="capitalize-first">{c.label}</span><X size={12} aria-hidden="true" />
        </button>))}
      <button type="button" onClick={onClear} data-testid="facet-clear" className="sticky right-0 shrink-0 whitespace-nowrap bg-white pl-2 text-xs font-bold text-[var(--brand-link)] underline-offset-2 hover:underline md:static md:pl-0">Clear all</button>
    </div>
  );
}

// quick chips: in stock, top brands (or top sub-categories on a brand page), price bands
export function QuickChips({ data, L, hideBrand }) {
  const f = data.facets || {};
  const avail = L.list("avail");
  const chip = (id, label, on, click, count) => (
    <button key={id} type="button" onClick={click} aria-pressed={on} data-testid={`quick-chip-${id}`}
      className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[13px] font-semibold transition-colors ${on ? "border-[var(--brand-button)] bg-[var(--brand-button)] text-[var(--brand-on-button)]" : "border-slate-300 bg-white text-slate-700 hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)]"}`}>
      {label}{count != null && <span className={`text-[11px] tabular-nums ${on ? "opacity-80" : "text-slate-400"}`}>{count.toLocaleString("en-AU")}</span>}
    </button>);
  const inS = (f.avail || []).find((o) => o.key === "in_stock");
  const chips = [];
  if (inS && inS.count) chips.push(chip("in-stock", "In stock", avail.includes("in_stock"), () => L.toggle("avail", "in_stock"), inS.count));
  if (!hideBrand && !L.list("brand").length) {
    [...(f.brand || [])].sort((a, b) => b.count - a.count).slice(0, 5).filter((b) => b.count)
      .forEach((b) => chips.push(chip(`brand-${b.slug}`, b.display_name, false, () => L.toggle("brand", b.slug), b.count)));
  } else if (f.category?.options?.length) {
    f.category.options.slice(0, 5).forEach((o) => chips.push(chip(`cat-${o.slug}`, o.display_name, false, () => L.set("cat", o.slug), o.count)));
  }
  const a = data.applied_filters || {};
  (f.price || []).filter((b) => b.count).slice(0, 4).forEach((b) => {
    const on = a.price_min === b.min && (a.price_max ?? null) === (b.max ?? null);
    chips.push(chip(`price-${b.key}`, b.max == null ? `${fmt(b.min)}+` : b.min === 0 ? `Under ${fmt(b.max)}` : `${fmt(b.min)}–${fmt(b.max)}`, on,
      () => (on ? L.setPrice(null, null) : L.setPrice(b.min, b.max)), b.count));
  });
  if (!chips.length) return null;
  return <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none]" data-testid="quick-chips" role="toolbar" aria-label="Quick filters">{chips}</div>;
}

// zero results: suggest which single filter to drop (counts from the facet engine)
export function EmptyState({ data, chips, L, prefix, children }) {
  const relax = data.facets?.relax || {};
  const tips = chips.filter((c) => c.dim && relax[c.dim]).map((c) => ({ ...c, n: relax[c.dim] }))
    .sort((x, y) => y.n - x.n).filter((c, i, arr) => arr.findIndex((z) => z.dim === c.dim) === i).slice(0, 3);
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center" data-testid={`${prefix}-empty`}>
      <div className="text-lg font-extrabold text-[var(--brand-primary)]">No products match all of these filters</div>
      {children}
      {tips.length > 0 && (
        <div className="mt-5 flex flex-wrap justify-center gap-2" data-testid="empty-suggestions">
          {tips.map((t) => (
            <Fragment key={t.id}>
              <button type="button" onClick={t.off} data-testid={`empty-relax-${t.dim.replace(/[^a-z0-9]+/gi, "-")}`}
                className="rounded-full bg-[var(--brand-button)] px-4 py-2 text-sm font-bold text-[var(--brand-on-button)] hover:bg-[var(--brand-button-hover)]">
                Remove “{t.label}” to see {t.n.toLocaleString("en-AU")} more
              </button>
            </Fragment>))}
        </div>)}
      {chips.length > 0 && <button type="button" onClick={L.clearAll} className="mt-4 block w-full text-sm font-bold text-[var(--brand-link)] hover:underline" data-testid="empty-clear-all">Clear all filters</button>}
    </div>
  );
}
